// src/components/deck_subdeck/ManageGroupsModal.tsx
import React, { useState, useRef } from 'react';
import { X, Edit, Trash2, Plus, GripVertical, Check, Upload, Image } from 'lucide-react';
import { SubdeckGroup } from './SubdeckGroupContainer';
import { GROUP_ICONS, getIconComponent } from './groupIcons';
import {
    DndContext, closestCenter,
    KeyboardSensor, PointerSensor,
    useSensor, useSensors, DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext,
    sortableKeyboardCoordinates, verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ── Paleta de cores GRAIA ─────────────────────────────────────────────
export const GROUP_COLORS = [
    // Verdes GRAIA
    { hex: '#0D3B0E', label: 'Verde GRAIA' },
    { hex: '#184C18', label: 'Verde Floresta' },
    { hex: '#2F7130', label: 'Verde Médio' },
    { hex: '#4A9B4B', label: 'Verde Claro' },
    { hex: '#6BBF6D', label: 'Verde Sage' },
    { hex: '#52A052', label: 'Verde Suave' },
    // Azuis
    { hex: '#1e3545', label: 'Azul Noturno' },
    { hex: '#2a4a5a', label: 'Azul Prussiano' },
    { hex: '#1A5276', label: 'Azul Royal' },
    { hex: '#2E86AB', label: 'Azul Oceano' },
    { hex: '#3d6b80', label: 'Azul Médio' },
    { hex: '#5C8A9A', label: 'Azul Aço' },
    { hex: '#2471A3', label: 'Azul Cobalto' },
    // Teais
    { hex: '#0E6655', label: 'Verde Água' },
    { hex: '#2D6B6B', label: 'Teal Escuro' },
    { hex: '#3D8080', label: 'Teal Médio' },
    { hex: '#4A9090', label: 'Teal Claro' },
    // Vermelhos / Rosas (Cardiologia, Emergência)
    { hex: '#7B1A1A', label: 'Vermelho Sangue' },
    { hex: '#A93226', label: 'Vermelho Médio' },
    { hex: '#C0392B', label: 'Vermelho Claro' },
    { hex: '#922B21', label: 'Borgonha' },
    { hex: '#8B2635', label: 'Vinho' },
    // Roxos / Lilás (Neurologia, Psiquiatria)
    { hex: '#4A235A', label: 'Roxo Escuro' },
    { hex: '#6C3483', label: 'Roxo Médio' },
    { hex: '#884EA0', label: 'Roxo Claro' },
    { hex: '#5B2C6F', label: 'Violeta' },
    { hex: '#7D3C98', label: 'Ametista' },
    // Laranjas / Âmbares (Gastro, Hepato)
    { hex: '#873600', label: 'Laranja Escuro' },
    { hex: '#B9770E', label: 'Âmbar Escuro' },
    { hex: '#C49A2A', label: 'Dourado' },
    { hex: '#D68910', label: 'Âmbar' },
    // Terrosos / Neutros
    { hex: '#556B2F', label: 'Oliva' },
    { hex: '#6B7C45', label: 'Oliva Claro' },
    { hex: '#8B7355', label: 'Terra' },
    { hex: '#6B4226', label: 'Café' },
    { hex: '#5C3317', label: 'Marrom Escuro' },
    // Cinzas / Grafite
    { hex: '#374151', label: 'Grafite' },
    { hex: '#4A5568', label: 'Ardósia' },
    { hex: '#566573', label: 'Cinza Azulado' },
    { hex: '#1A1A2E', label: 'Azul Marinho' },
    // Pinks (Gineco, Neonatal)
    { hex: '#6B2D3E', label: 'Bordô' },
    { hex: '#7B3F6E', label: 'Ameixa' },
    { hex: '#8E3A59', label: 'Rosa Escuro' },
    { hex: '#A04060', label: 'Rosa Médio' },
    // Carmesins / Infravermelhos
    { hex: '#5C0010', label: 'Carmesim Escuro' },
    { hex: '#8B0000', label: 'Vermelho Vivo' },
    { hex: '#B22222', label: 'Tijolo' },
    { hex: '#CD5C5C', label: 'Coral Médio' },
    // Índigo / Violeta profundo
    { hex: '#1A0050', label: 'Índigo Escuro' },
    { hex: '#2E1A6A', label: 'Violeta Noturno' },
    { hex: '#4B0082', label: 'Índigo Puro' },
    { hex: '#5A3E8C', label: 'Lavanda Escura' },
    // Ciano / Turquesa
    { hex: '#006064', label: 'Ciano Profundo' },
    { hex: '#00838F', label: 'Ciano Médio' },
    { hex: '#00ACC1', label: 'Turquesa' },
    { hex: '#26C6DA', label: 'Ciano Claro' },
    // Amarelos / Mostardas
    { hex: '#5C4800', label: 'Mostarda Escura' },
    { hex: '#7B6000', label: 'Dourado Profundo' },
    { hex: '#9C7700', label: 'Âmbar Médio' },
    { hex: '#B8860B', label: 'Ouro Antigo' },
    // Slate / Azul cinza
    { hex: '#2C3E50', label: 'Slate Escuro' },
    { hex: '#34495E', label: 'Slate Médio' },
    { hex: '#5D6D7E', label: 'Slate Claro' },
    { hex: '#7F8C8D', label: 'Cinza Neutro' },
    // Tons quentes adicionais
    { hex: '#4E342E', label: 'Marrom Profundo' },
    { hex: '#5D4037', label: 'Cacau' },
    { hex: '#6D4C41', label: 'Mogno' },
    { hex: '#795548', label: 'Terra Clara' },
];

interface ManageGroupsModalProps {
    deckId: string;
    groups: SubdeckGroup[];
    onClose: () => void;
    onGroupsChange: (groups: SubdeckGroup[]) => void;
    onToast: (message: string, type: 'success' | 'error') => void;
}

interface GroupFormData {
    name: string;
    description: string;
    iconType: 'lucide' | 'image';
    iconName: string;
    iconUrl: string;
    color: string;
}

const defaultForm: GroupFormData = {
    name: '', description: '',
    iconType: 'lucide', iconName: 'Layers', iconUrl: '',
    color: '#0D3B0E'
};

// ── Sortable item ─────────────────────────────────────────────────────
interface SortableGroupItemProps {
    group: SubdeckGroup;
    onEdit: (g: SubdeckGroup) => void;
    onDelete: (id: string) => void;
}

const SortableGroupItem: React.FC<SortableGroupItemProps> = ({ group, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group._id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

    const IconComp = group.iconType === 'lucide' ? getIconComponent(group.iconName) : null;

    return (
        <div ref={setNodeRef} style={style} className="sdp-mg-item">
            <div className="sdp-mg-item-drag" {...attributes} {...listeners}>
                <GripVertical size={16} />
            </div>
            <div className="sdp-mg-item-icon-wrap" style={{ background: group.color + '22', borderRadius: 8, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {group.iconType === 'image' && group.iconUrl
                    ? <img src={group.iconUrl} alt="" style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 4 }} />
                    : IconComp && <IconComp size={20} color={group.color} />
                }
            </div>
            <span className="sdp-mg-item-name" style={{ borderLeft: `3px solid ${group.color}`, paddingLeft: 8 }}>
                {group.name}
                {group.description && <small className="sdp-mg-item-desc">{group.description}</small>}
            </span>
            <span className="sdp-mg-item-count">{group.subdeckCount} subdeck{group.subdeckCount !== 1 ? 's' : ''}</span>
            <button className="sdp-mg-item-btn" onClick={() => onEdit(group)} title="Editar">
                <Edit size={14} />
            </button>
            <button className="sdp-mg-item-btn sdp-mg-item-btn--danger" onClick={() => onDelete(group._id)} title="Excluir">
                <Trash2 size={14} />
            </button>
        </div>
    );
};

// ── Modal principal ───────────────────────────────────────────────────
const ManageGroupsModal: React.FC<ManageGroupsModalProps> = ({
    deckId, groups: initialGroups, onClose, onGroupsChange, onToast
}) => {
    const [groups, setGroups] = useState<SubdeckGroup[]>(initialGroups);
    const [form, setForm] = useState<GroupFormData>(defaultForm);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const token = () => localStorage.getItem('accessToken') || '';

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = groups.findIndex(g => g._id === active.id);
        const newIndex = groups.findIndex(g => g._id === over.id);
        const reordered = arrayMove(groups, oldIndex, newIndex).map((g, i) => ({ ...g, order: i }));
        setGroups(reordered);
        onGroupsChange(reordered);
        try {
            await fetch(`/api/decks/${deckId}/groups/reorder`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                body: JSON.stringify({ items: reordered.map(g => ({ _id: g._id, order: g.order })) })
            });
        } catch {
            onToast('Erro ao reordenar grupos', 'error');
        }
    };

    const openCreate = () => {
        setEditingId(null);
        setForm(defaultForm);
        setShowForm(true);
    };

    const openEdit = (g: SubdeckGroup) => {
        setEditingId(g._id);
        setForm({
            name: g.name,
            description: g.description || '',
            iconType: g.iconType || 'lucide',
            iconName: g.iconName || 'Layers',
            iconUrl: g.iconUrl || '',
            color: g.color
        });
        setShowForm(true);
    };

    // Upload de ícone para Cloudinary
    const handleIconUpload = async (file: File, groupId?: string) => {
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('icon', file);
            const targetId = groupId || 'new_' + Date.now();
            const r = await fetch(`/api/upload/group-icon/${targetId}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token()}` },
                body: formData
            });
            if (r.ok) {
                const data = await r.json();
                setForm(p => ({ ...p, iconType: 'image', iconUrl: data.iconUrl }));
            } else {
                onToast('Erro ao fazer upload do ícone', 'error');
            }
        } catch {
            onToast('Erro ao fazer upload do ícone', 'error');
        } finally {
            setUploading(false);
        }
    };

    const handleSaveForm = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            const body = {
                name: form.name,
                description: form.description,
                iconType: form.iconType,
                iconName: form.iconName,
                iconUrl: form.iconUrl,
                color: form.color
            };
            if (editingId) {
                const r = await fetch(`/api/decks/${deckId}/groups/${editingId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify(body)
                });
                if (r.ok) {
                    const updated = await r.json();
                    const newGroups = groups.map(g => g._id === editingId
                        ? { ...g, ...updated, subdeckCount: g.subdeckCount, cardCount: g.cardCount }
                        : g
                    );
                    setGroups(newGroups);
                    onGroupsChange(newGroups);
                    onToast('Grupo atualizado!', 'success');
                    setShowForm(false);
                } else {
                    const e = await r.json();
                    onToast(e.message || 'Erro ao editar grupo', 'error');
                }
            } else {
                const r = await fetch(`/api/decks/${deckId}/groups`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
                    body: JSON.stringify(body)
                });
                if (r.ok) {
                    const created = await r.json();
                    const newGroups = [...groups, created];
                    setGroups(newGroups);
                    onGroupsChange(newGroups);
                    onToast('Grupo criado!', 'success');
                    setShowForm(false);
                } else {
                    const e = await r.json();
                    onToast(e.message || 'Erro ao criar grupo', 'error');
                }
            }
        } catch {
            onToast('Erro ao salvar grupo', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (groupId: string) => {
        setSaving(true);
        try {
            const r = await fetch(`/api/decks/${deckId}/groups/${groupId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token()}` }
            });
            if (r.ok) {
                const newGroups = groups.filter(g => g._id !== groupId);
                setGroups(newGroups);
                onGroupsChange(newGroups);
                onToast('Grupo excluído. Subdecks movidos para "Sem grupo".', 'success');
                setDeleteConfirmId(null);
            } else {
                onToast('Erro ao excluir grupo', 'error');
            }
        } catch {
            onToast('Erro ao excluir grupo', 'error');
        } finally {
            setSaving(false);
        }
    };

    const PreviewIcon = form.iconType === 'lucide' ? getIconComponent(form.iconName) : null;

    return (
        <div className="sdp-dialog-overlay" onClick={onClose}>
            <div className="sdp-dialog sdp-manage-groups-dialog" onClick={e => e.stopPropagation()}>
                <div className="sdp-dialog-header">
                    <h2 className="sdp-dialog-title">Gerenciar grupos</h2>
                    <button className="sdp-dialog-close" onClick={onClose}><X size={18} /></button>
                </div>

                {/* Lista com DnD */}
                {!showForm && (
                    <div className="sdp-mg-list">
                        {groups.length === 0 && (
                            <p className="sdp-mg-empty">Nenhum grupo criado ainda.</p>
                        )}
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={groups.map(g => g._id)} strategy={verticalListSortingStrategy}>
                                {groups.map(g => (
                                    deleteConfirmId === g._id ? (
                                        <div key={g._id} className="sdp-mg-delete-confirm">
                                            <span>Excluir "{g.name}"? Os subdecks ficarão sem grupo.</span>
                                            <div className="sdp-mg-delete-actions">
                                                <button className="sdp-mg-btn-cancel" onClick={() => setDeleteConfirmId(null)} disabled={saving}>Cancelar</button>
                                                <button className="sdp-mg-btn-delete" onClick={() => handleDelete(g._id)} disabled={saving}>
                                                    {saving ? 'Excluindo...' : 'Excluir'}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <SortableGroupItem key={g._id} group={g} onEdit={openEdit} onDelete={id => setDeleteConfirmId(id)} />
                                    )
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>
                )}

                {/* Formulário criar/editar */}
                {showForm && (
                    <div className="sdp-mg-form">
                        <h3 className="sdp-mg-form-title">{editingId ? 'Editar grupo' : 'Novo grupo'}</h3>

                        {/* Nome */}
                        <div className="sdp-mg-form-row">
                            <label className="sdp-mg-form-label">Nome *</label>
                            <input className="sdp-mg-form-input" type="text" value={form.name}
                                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="Nome do grupo" maxLength={80} autoFocus />
                        </div>

                        {/* Descrição */}
                        <div className="sdp-mg-form-row">
                            <label className="sdp-mg-form-label">Descrição</label>
                            <input className="sdp-mg-form-input" type="text" value={form.description}
                                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                                placeholder="Descrição opcional" maxLength={120} />
                        </div>

                        {/* Ícone */}
                        <div className="sdp-mg-form-row">
                            <label className="sdp-mg-form-label">Ícone</label>
                            <div className="sdp-mg-icon-tabs">
                                <button
                                    className={`sdp-mg-icon-tab ${form.iconType === 'lucide' ? 'sdp-mg-icon-tab--active' : ''}`}
                                    onClick={() => setForm(p => ({ ...p, iconType: 'lucide' }))}
                                    type="button"
                                >
                                    Ícones
                                </button>
                                <button
                                    className={`sdp-mg-icon-tab ${form.iconType === 'image' ? 'sdp-mg-icon-tab--active' : ''}`}
                                    onClick={() => setForm(p => ({ ...p, iconType: 'image' }))}
                                    type="button"
                                >
                                    Imagem
                                </button>
                            </div>

                            {form.iconType === 'lucide' && (
                                <div className="sdp-mg-icon-grid">
                                    {GROUP_ICONS.map(entry => {
                                        const Ic = entry.component;
                                        return (
                                            <button
                                                key={entry.name}
                                                type="button"
                                                title={entry.label}
                                                className={`sdp-mg-icon-btn ${form.iconName === entry.name ? 'sdp-mg-icon-btn--selected' : ''}`}
                                                onClick={() => setForm(p => ({ ...p, iconName: entry.name }))}
                                                style={form.iconName === entry.name ? { borderColor: form.color, background: form.color + '18' } : {}}
                                            >
                                                <Ic size={20} color={form.iconName === entry.name ? form.color : undefined} />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {form.iconType === 'image' && (
                                <div className="sdp-mg-upload-area">
                                    {form.iconUrl ? (
                                        <div className="sdp-mg-upload-preview">
                                            {/* Preview contextual — como fica no cabeçalho do grupo */}
                                            <div className="sdp-mg-upload-context-preview">
                                                <div className="sdp-mg-upload-context-icon" style={{ background: form.color + '18' }}>
                                                    <img src={form.iconUrl} alt="ícone" />
                                                </div>
                                                <div className="sdp-mg-upload-context-text">
                                                    <span className="sdp-mg-upload-context-name">{form.name || 'Nome do grupo'}</span>
                                                    <span className="sdp-mg-upload-context-meta">prévia</span>
                                                </div>
                                            </div>
                                            <button className="sdp-mg-upload-remove" onClick={() => setForm(p => ({ ...p, iconUrl: '', iconType: 'lucide' }))} type="button">
                                                <X size={14} /> Remover imagem
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            className="sdp-mg-upload-btn"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploading}
                                            type="button"
                                        >
                                            {uploading
                                                ? <><Upload size={16} /> Enviando...</>
                                                : <><Image size={16} /> Escolher imagem</>
                                            }
                                        </button>
                                    )}
                                    <p className="sdp-mg-upload-hint">
                                        Use PNG ou SVG com <strong>fundo transparente</strong>. O ícone será exibido em 40×40px — mantenha o desenho simples e centralizado.
                                    </p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                        style={{ display: 'none' }}
                                        onChange={e => {
                                            const file = e.target.files?.[0];
                                            if (file) handleIconUpload(file, editingId || undefined);
                                        }}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Cor */}
                        <div className="sdp-mg-form-row">
                            <label className="sdp-mg-form-label">Cor</label>
                            <div className="sdp-mg-color-palette">
                                {GROUP_COLORS.map(c => (
                                    <button
                                        key={c.hex}
                                        type="button"
                                        title={c.label}
                                        className={`sdp-mg-color-swatch ${form.color === c.hex ? 'sdp-mg-color-swatch--selected' : ''}`}
                                        style={{ background: c.hex }}
                                        onClick={() => setForm(p => ({ ...p, color: c.hex }))}
                                    >
                                        {form.color === c.hex && <Check size={12} color="#fff" />}
                                    </button>
                                ))}
                            </div>
                            <div className="sdp-mg-color-preview">
                                {PreviewIcon && form.iconType === 'lucide' && (
                                    <div className="sdp-mg-color-preview-icon" style={{ background: form.color + '22' }}>
                                        <PreviewIcon size={22} color={form.color} />
                                    </div>
                                )}
                                <span className="sdp-mg-color-hex">{form.color}</span>
                            </div>
                        </div>

                        <div className="sdp-mg-form-actions">
                            <button className="sdp-mg-btn-cancel" onClick={() => setShowForm(false)} disabled={saving}>Cancelar</button>
                            <button className="sdp-mg-btn-save" onClick={handleSaveForm} disabled={saving || !form.name.trim()}>
                                {saving ? 'Salvando...' : <><Check size={14} /> {editingId ? 'Salvar' : 'Criar grupo'}</>}
                            </button>
                        </div>
                    </div>
                )}

                <div className="sdp-dialog-footer sdp-mg-footer">
                    {!showForm && (
                        <button className="sdp-mg-btn-new" onClick={openCreate}>
                            <Plus size={14} /> Novo grupo
                        </button>
                    )}
                    <button className="sdp-dialog-btn sdp-dialog-btn--cancel" onClick={onClose}>Fechar</button>
                </div>
            </div>
        </div>
    );
};

export default ManageGroupsModal;
